// src/scene/stages/ProjectsStage.tsx
/**
 * Projects Stage
 *
 * Portfolio projects displayed in 2x2 grid layout.
 * Uses StageShell for consistent positioning.
 */

import { Container, Text } from '@react-three/uikit'
import { useControls, folder } from 'leva'
import { StageShell } from '../StageShell'
import { useResponsiveLayout, useDesktopLayout } from '../useResponsiveLayout'
import {
  ui,
  Panel,
  HStack,
  Badge,
  Tag,
  PanelHeader,
  useHoverScale,
} from '../ui'

// ============================================================================
// PROJECTS DATA
// ============================================================================

const PROJECTS = [
  {
    name: 'Omote',
    description: 'Open source AI character SDK for the web. Pure ONNX Runtime inference for real-time lip sync, speech recognition, and voice detection. Runs entirely in-browser with WebGPU and WASM, zero cloud dependencies.',
    tags: ['ONNX', 'WebGPU', 'WASM', 'TypeScript'],
    accentColor: ui.colors.success,
  },
  {
    name: 'Interactive Portfolio',
    description: 'This site: a live AI avatar you can talk to. Omote powers on-device ML, React Three Fiber renders the 3D scene, and AWS Strands orchestrates the conversational agent with streaming TTS.',
    tags: ['AWS Strands', 'ElevenLabs', 'Omote', 'R3F'],
    accentColor: ui.colors.primary,
  },
  {
    name: '3DPC',
    description: 'Back in active development. A fully 3D PC building simulator with interactive UI and animations. Custom rendering pipeline, physics-based part snapping, and real hardware data.',
    tags: ['React Three Fiber', 'Three.js', 'AWS CDK'],
    accentColor: ui.colors.secondary,
  },
]

// ============================================================================
// PROJECTS STAGE
// ============================================================================

// Stage 3 (Projects) UI defaults
const STAGE3_MOBILE_POSITION: [number, number, number] = [0, 0.245, 2]
const STAGE3_TABLET_POSITION: [number, number, number] = [0, 0.245, 2]  // Adjust for tablet
const STAGE3_UI_ROTATION: [number, number, number] = [-0.15, 0, 0]

export function ProjectsStage() {
  const { isTablet } = useResponsiveLayout()
  const { isDesktopLayout } = useDesktopLayout()

  // Select position based on breakpoint
  const defaultPosition = isTablet ? STAGE3_TABLET_POSITION : STAGE3_MOBILE_POSITION

  // Leva controls for Stage 3 UI position and rotation (mobile/tablet mode)
  const {
    uiPosX, uiPosY, uiPosZ,
    uiRotX, uiRotY, uiRotZ,
  } = useControls('Stage 3 UI', {
    Position: folder({
      uiPosX: { value: defaultPosition[0], min: -5, max: 5, step: 0.01 },
      uiPosY: { value: defaultPosition[1], min: -5, max: 5, step: 0.01 },
      uiPosZ: { value: defaultPosition[2], min: -5, max: 5, step: 0.01 },
    }),
    Rotation: folder({
      uiRotX: { value: STAGE3_UI_ROTATION[0], min: -Math.PI, max: Math.PI, step: 0.001 },
      uiRotY: { value: STAGE3_UI_ROTATION[1], min: -Math.PI, max: Math.PI, step: 0.001 },
      uiRotZ: { value: STAGE3_UI_ROTATION[2], min: -Math.PI, max: Math.PI, step: 0.001 },
    }),
  })

  return (
    <StageShell
      stageId="projects"
      panelWidth={400}
      panelHeight={585}
      scale={0.7}
      panel={<ProjectsPanel isDesktop={isDesktopLayout} />}
      panelPosition={[uiPosX, uiPosY, uiPosZ]}
      panelRotation={[uiRotX, uiRotY, uiRotZ]}
    />
  )
}

// ============================================================================
// PROJECTS PANEL
// ============================================================================

interface ProjectsPanelProps {
  isDesktop: boolean
}

function ProjectsPanel({ isDesktop }: ProjectsPanelProps) {
  return (
    <Panel width={400} height={585} padding={ui.space.xl}>
      <PanelHeader
        title="Projects"
        badge={!isDesktop ? <Badge variant="accent">ACTIVE</Badge> : undefined}
      />

      {/* Vertical layout - consistent with other panels */}
      <Container flexDirection="column" gap={ui.space.lg} flexGrow={1}>
        {PROJECTS.slice(0, 3).map((project, index) => (
          <ProjectCard key={index} project={project} />
        ))}
      </Container>
    </Panel>
  )
}

// ============================================================================
// PROJECT CARD
// ============================================================================

function ProjectCard({ project }: { project: typeof PROJECTS[0] }) {
  const { isActive, ...hoverScale } = useHoverScale()

  return (
    <Container
      flexDirection="column"
      width="100%"
      flexGrow={1}
      padding={ui.space.lg}
      gap={ui.space.lg}
      backgroundColor={isActive ? ui.colors.bg.card : ui.colors.bg.surface}
      borderRadius={ui.radius.md}
      borderTopWidth={1}
      borderBottomWidth={1}
      borderRightWidth={1}
      borderLeftWidth={3}
      borderColor={isActive ? project.accentColor : ui.colors.border.subtle}
      hover={{
        backgroundColor: ui.colors.bg.card,
        borderColor: project.accentColor,
      }}
      {...hoverScale}
    >
      {/* === HEADER === */}
      <Text
        fontSize={ui.font.size.sm}
        fontWeight={ui.font.weight.bold}
        color={ui.colors.text.primary}
      >
        {project.name}
      </Text>

      {/* === BODY (flex to fill space) === */}
      <Container flexDirection="column" flexGrow={1}>
        <Text
          fontSize={ui.font.size.xs}
          fontWeight={ui.font.weight.normal}
          color={ui.colors.text.secondary}
          lineHeight={ui.font.leading.normal}
        >
          {project.description}
        </Text>
      </Container>

      {/* === FOOTER === */}
      <HStack gap={ui.space.sm}>
        {project.tags.slice(0, 4).map((tag) => (
          <Tag key={tag}>{tag}</Tag>
        ))}
      </HStack>
    </Container>
  )
}

export default ProjectsStage
